import { useState } from "react";
import { useContext } from "react";
import moment from "moment";
import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import "./TransferirPagamento.scss";

// Extrai um ObjectId de uma string (ID puro ou link "/clientes/<id>").
const extrairId = (valor) => {
  if (!valor) return null;
  const trimmed = valor.trim();
  const match = trimmed.match(/\/clientes\/([a-f0-9]{24})/i);
  if (match) return match[1];
  if (/^[a-f0-9]{24}$/i.test(trimmed)) return trimmed;
  return null;
};

const formatarValor = (v) =>
  `${new Intl.NumberFormat("pt-PT").format(v || 0)} CVE`;

function TransferirPagamento() {
  const auth = useContext(AuthContext);
  const clinicaCtx = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [clienteInput, setClienteInput] = useState("");
  const [dados, setDados] = useState(null); // { entradas, contas }
  const [orcamentoSel, setOrcamentoSel] = useState(""); // orcamentoId
  // Várias entradas de uma vez. O servidor aplica-as todas na MESMA transação:
  // ou entram todas, ou nenhuma.
  const [entradasSel, setEntradasSel] = useState([]);
  const [confirmando, setConfirmando] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const clienteId = extrairId(clienteInput);

  const contaEscolhida = dados?.contas.find(
    (c) => c.orcamentoId === orcamentoSel,
  );
  const entradasEscolhidas = (dados?.entradas || []).filter((e) =>
    entradasSel.includes(e._id),
  );
  const totalSelecionado = entradasEscolhidas.reduce(
    (s, e) => s + (e.valor || 0),
    0,
  );

  const alternarEntrada = (id) =>
    setEntradasSel((atual) =>
      atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id],
    );

  const validacao = (() => {
    if (!contaEscolhida || entradasEscolhidas.length === 0) return null;
    /* A soma, não cada uma de per si: duas entradas de 5.000 cabem
       individualmente numa conta com 8.000 em aberto, mas juntas não. É a mesma
       verificação que o servidor faz — aqui só para avisar antes de tentar. */
    if (totalSelecionado > contaEscolhida.totalEmAberto) {
      return `O total selecionado (${formatarValor(
        totalSelecionado,
      )}) excede o total em aberto da conta (${formatarValor(
        contaEscolhida.totalEmAberto,
      )}).`;
    }
    return null;
  })();

  const podeExecutar =
    contaEscolhida && entradasEscolhidas.length > 0 && !validacao && !isLoading;

  const handleCarregarDados = async () => {
    if (!clienteId) {
      toast.error("Introduz um ID ou link de cliente válido.");
      return;
    }
    // Não limpar `resultado` aqui: este método é reutilizado após uma
    // transferência bem-sucedida para refrescar os dados, e queremos manter a
    // mensagem de sucesso visível. O `resultado` é limpo ao alterar o cliente.
    setDados(null);
    setOrcamentoSel("");
    setEntradasSel([]);
    startLoading();
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/transferenciaentrada/dados/${clienteId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setDados(resposta);
      if (resposta.entradas.length === 0) {
        toast("Este cliente não tem entradas recebidas ativas.");
      }
      if (resposta.contas.length === 0) {
        toast("Este cliente não tem contas com valor em aberto.");
      }
    } catch (err) {
      toast.error(err.message || "Erro ao carregar dados do cliente.");
    } finally {
      stopLoading();
    }
  };

  const handleExecutar = async () => {
    startLoading();
    setIsLoading(true);
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/transferenciaentrada`,
        "POST",
        JSON.stringify({
          clienteId,
          orcamentoId: orcamentoSel,
          entradaIds: entradasSel,
          utilizador: auth.userId,
          clinica: clinicaCtx.clinica._id,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      setResultado({ sucesso: true, dados: resposta });
      setConfirmando(false);
      toast.success(resposta?.mensagem || "Pagamento transferido com sucesso.");
      // Recarregar os dados para refletir o novo estado
      handleCarregarDados();
    } catch (err) {
      setResultado({
        sucesso: false,
        mensagem: err.message || "Erro desconhecido.",
      });
      setConfirmando(false);
    } finally {
      setIsLoading(false);
      stopLoading();
    }
  };

  return (
    <div className="transferir-pagamento">
      <div className="transferir-pagamento__explicacao">
        <h3>O que faz este script?</h3>
        <p>
          Move o <strong>pagamento registado numa entrada</strong> para a conta
          corrente de um orçamento. As parcelas são tratadas como num
          recebimento (pagamento exato, divisão ou fusão). A entrada original é{" "}
          <strong>cancelada e desativada</strong>, e o documento eletrónico (se
          existir) é religado à parcela resultante.
        </p>
        <div className="transferir-pagamento__aviso">
          <span>⚠</span>
          <span>
            Operação sensível sobre dados financeiros. Confirma sempre o cliente,
            o orçamento e a entrada antes de executar.
          </span>
        </div>
      </div>

      {/* 1. Cliente */}
      <div className="transferir-pagamento__campo">
        <label className="transferir-pagamento__label">Cliente</label>
        <div className="transferir-pagamento__cliente-row">
          <input
            className="transferir-pagamento__input"
            type="text"
            placeholder="ID ou link da ficha do cliente"
            value={clienteInput}
            onChange={(e) => {
              setClienteInput(e.target.value);
              setDados(null);
              setResultado(null);
            }}
            disabled={isLoading}
          />
          <button
            className="transferir-pagamento__btn transferir-pagamento__btn--carregar"
            onClick={handleCarregarDados}
            disabled={isLoading || !clienteId}
          >
            Carregar
          </button>
        </div>
        {clienteId && (
          <span className="transferir-pagamento__id">ID: {clienteId}</span>
        )}
      </div>

      {dados && (
        <>
          {/* 2. Orçamento / Conta */}
          <div className="transferir-pagamento__campo">
            <label className="transferir-pagamento__label">
              Orçamento (conta em aberto)
            </label>
            {dados.contas.length === 0 ? (
              <p className="transferir-pagamento__vazio">
                Sem contas com valor em aberto.
              </p>
            ) : (
              <select
                className="transferir-pagamento__select"
                value={orcamentoSel}
                onChange={(e) => setOrcamentoSel(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Selecione um orçamento…</option>
                {dados.contas.map((c) => (
                  <option key={c.contaId} value={c.orcamentoId}>
                    Orçamento nº {c.orcamentoNumero ?? "—"} (cód.{" "}
                    {c.orcamentoCodigo ?? "—"}) — em aberto:{" "}
                    {formatarValor(c.totalEmAberto)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 3. Entradas */}
          <div className="transferir-pagamento__campo">
            <label className="transferir-pagamento__label">
              Entradas a transferir
              {entradasSel.length > 0 && (
                <span className="transferir-pagamento__label-extra">
                  {" "}
                  — {entradasSel.length} selecionada
                  {entradasSel.length > 1 ? "s" : ""}, total{" "}
                  {formatarValor(totalSelecionado)}
                </span>
              )}
            </label>
            {dados.entradas.length === 0 ? (
              <p className="transferir-pagamento__vazio">
                Sem entradas recebidas ativas.
              </p>
            ) : (
              <div className="transferir-pagamento__entradas">
                {/* As entradas com recibo eletrónico deixaram de ser
                    bloqueadas (2026-09-15): a transferência passou a mover o
                    recibo, a fatura e os cancelamentos com o pagamento. Ficam
                    assinaladas na mesma — quem transfere deve saber o que está
                    a mover. As de outra unidade nem chegam aqui: o servidor não
                    as devolve. */}
                {dados.entradas.map((e) => (
                  <label
                    key={e._id}
                    title={
                      e.temRecibo
                        ? "Tem recibo eletrónico (RCE). A fatura, o recibo e os cancelamentos passam para a parcela."
                        : undefined
                    }
                    className={`transferir-pagamento__entrada ${
                      entradasSel.includes(e._id)
                        ? "transferir-pagamento__entrada--sel"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      value={e._id}
                      checked={entradasSel.includes(e._id)}
                      onChange={() => alternarEntrada(e._id)}
                      disabled={isLoading}
                    />
                    <span className="transferir-pagamento__entrada__desc">
                      {e.descricao || "(sem descrição)"}
                    </span>
                    <span className="transferir-pagamento__entrada__valor">
                      {formatarValor(e.valor)}
                    </span>
                    <span className="transferir-pagamento__entrada__data">
                      {e.dataPagamento
                        ? moment(e.dataPagamento).format("DD/MM/YYYY")
                        : "—"}
                    </span>
                    <span className="transferir-pagamento__entrada__doc">
                      {e.temRecibo
                        ? "📄 fatura + recibo"
                        : e.temDocumento
                          ? "📄 c/ fatura"
                          : "sem documento"}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Validação */}
          {validacao && (
            <p className="transferir-pagamento__erro-validacao">{validacao}</p>
          )}

          {/* Ações */}
          {!confirmando && (
            <button
              className="transferir-pagamento__btn transferir-pagamento__btn--preparar"
              onClick={() => setConfirmando(true)}
              disabled={!podeExecutar}
            >
              Preparar transferência
            </button>
          )}

          {confirmando && (
            <div className="transferir-pagamento__confirmacao">
              {/* Listar uma a uma, e não só o total: quem confirma tem de poder
                  reconhecer cada entrada que vai desaparecer. */}
              <p>
                Transferir <strong>{formatarValor(totalSelecionado)}</strong> em{" "}
                {entradasEscolhidas.length}{" "}
                {entradasEscolhidas.length === 1 ? "entrada" : "entradas"} para o
                orçamento nº {contaEscolhida?.orcamentoNumero}?{" "}
                {entradasEscolhidas.length === 1
                  ? "A entrada será cancelada e desativada."
                  : "As entradas serão canceladas e desativadas."}
              </p>
              <ul className="transferir-pagamento__confirmacao__lista">
                {entradasEscolhidas.map((e) => (
                  <li key={e._id}>
                    {e.descricao || "(sem descrição)"} — {formatarValor(e.valor)}
                  </li>
                ))}
              </ul>
              <div className="transferir-pagamento__confirmacao__acoes">
                <button
                  className="transferir-pagamento__btn transferir-pagamento__btn--cancelar"
                  onClick={() => setConfirmando(false)}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  className="transferir-pagamento__btn transferir-pagamento__btn--confirmar"
                  onClick={handleExecutar}
                  disabled={isLoading}
                >
                  {isLoading ? "A processar…" : "Confirmar e transferir"}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {resultado && (
        <div
          className={`transferir-pagamento__resultado ${
            resultado.sucesso
              ? "transferir-pagamento__resultado--sucesso"
              : "transferir-pagamento__resultado--erro"
          }`}
        >
          {resultado.sucesso ? (
            <strong>{resultado.dados?.mensagem || "Concluído."}</strong>
          ) : (
            <>
              <strong>Erro ao transferir.</strong>
              <p>{resultado.mensagem}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TransferirPagamento;
